import * as React from 'react';

import InfoNote from 'components/info-note/InfoNote';

import CampaignPayoutsListSummary from './summary/CampaignPayoutsListSummary';
import CampaignPayoutsListUnallocated from './unallocated/CampaignPayoutsListUnallocated';
import CampaignPayoutsListItems from './items/CampaignPayoutsListItems';

import { renderPrice } from 'utils/helper/price';
import { __t, applyTranslations } from 'utils/language';
import { getAcceptedOfferBudget, getTotalVariablePayment, getUnallocatedAmount } from 'utils/payouts';

import './campaign-payouts-list.scss';

import type {
  Campaign,
  PaymentConfirmationModalParams,
  PaymentInfo,
  Payout,
  Subcampaign
} from 'types';

applyTranslations(require('./campaign-payouts-list.i18n.json'));

type Props = {
  payouts: Array<Payout>,
  paymentInfo: PaymentInfo,
  campaign: Campaign,
  subcampaign: Subcampaign,
  isNegotiationReopened: boolean,
  otherArrangement: boolean,
  onUnreleasePartialPayout: (payoutId: number, campaignId: number, subcampaignId: number) => void,
  onShowConfirmationModal: (params: PaymentConfirmationModalParams) => void,
  onHideConfirmationModal: () => void
};

const msgKey = 'campaign_payouts_list';

class CampaignPayoutsList extends React.PureComponent<Props> {
  getTotalReleased = (): number => {
    const { payouts } = this.props;
    let total = 0;

    payouts.forEach(payout => {
      total += payout.releaseDate ? payout.amount : 0;
    });

    return total;
  };

  renderWarning(): React.ReactNode {
    return (
      <div className="campaign-payouts-list__warning">
        <InfoNote type="warning">
          {__t(`${msgKey}.messages.installments_greater_than_total`)}
        </InfoNote>
      </div>
    );
  }

  renderSummary(): React.ReactNode {
    const { subcampaign, paymentInfo, otherArrangement } = this.props;
    const totalReleased = this.getTotalReleased();
    const subcampaignBudget = getAcceptedOfferBudget(subcampaign);
    const variablePayment = getTotalVariablePayment(paymentInfo);

    return (
      <CampaignPayoutsListSummary
        data={{
          payout: {
            label: __t(`${msgKey}.labels.total_payout`),
            total: otherArrangement ?
              renderPrice(variablePayment, { defaultValue: String(paymentInfo.value) })
              : paymentInfo.value
          },
          released: {
            label: __t(`${msgKey}.labels.released`),
            total: renderPrice(totalReleased)
          },
          remaining: {
            label: __t(`${msgKey}.labels.to_be_released`),
            total: renderPrice((subcampaignBudget - totalReleased) + variablePayment)
          }
        }}
      />
    );
  }

  renderItems(): React.ReactNode {
    const {
      payouts,
      campaign,
      subcampaign,
      isNegotiationReopened,
      onUnreleasePartialPayout,
      onShowConfirmationModal,
      onHideConfirmationModal
    } = this.props;

    return (
      <CampaignPayoutsListItems
        headerItems={[
          __t(`${msgKey}.labels.note`),
          __t(`${msgKey}.labels.amount`),
          __t(`${msgKey}.labels.date_scheduled`),
          __t(`${msgKey}.labels.date_released`),
          __t(`${msgKey}.labels.payment_expected`),
          __t(`${msgKey}.labels.status`)
        ]}
        listItems={payouts}
        campaignId={campaign.id}
        subcampaign={subcampaign}
        isNegotiationReopened={isNegotiationReopened}
        onUnreleasePartialPayout={onUnreleasePartialPayout}
        onShowConfirmationModal={onShowConfirmationModal}
        onHideConfirmationModal={onHideConfirmationModal}
        isBrand
      />
    );
  }

  renderUnallocated(unallocatedAmount: number): React.ReactNode {
    const { campaign, subcampaign, paymentInfo } = this.props;

    return (
      <CampaignPayoutsListUnallocated
        campaign={campaign}
        subcampaign={subcampaign}
        paymentInfo={paymentInfo}
        remainingTotal={unallocatedAmount}
        isBrand
      />
    );
  }

  render(): React.ReactNode {
    const { payouts, paymentInfo, subcampaign } = this.props;
    const unallocatedAmount = getUnallocatedAmount(payouts, subcampaign, paymentInfo);

    return (
      <section
        className="campaign-payouts-list"
        data-qa="campaign-payouts-list"
      >
        {this.renderSummary()}
        {this.renderItems()}
        {
          (unallocatedAmount > 0 || unallocatedAmount < 0) &&
            this.renderUnallocated(unallocatedAmount)
        }
        {
          unallocatedAmount < 0 &&
            this.renderWarning()
        }
      </section>
    );
  }
}

export default CampaignPayoutsList;
